import React, { useEffect, useState } from "react";
import Modal from "@material-ui/core/Modal";
import { FaEdit, FaTimes, FaCalendarAlt, FaSave } from "react-icons/fa";

import { formatObservationDateTime } from "../../helpers/observationDateUtils";
import {
  toDatetimeLocalValue,
  toLocalISOString,
} from "./observationDateUtils";

function ServiceOrderCardEditModal({
  show,
  obs,
  onHide,
  onSave,
  t,
  dialogTitle,
  notesLabel,
}) {
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  const isConfirmed = Boolean(obs && obs.confirmed);
  const title =
    (obs && (obs.operationName || obs.operation_name || obs.description)) ||
    t("Ordem de serviço");
  const farmLabel = obs && (obs.service_farm_names || obs.site_farm_name);

  useEffect(() => {
    if (!show || !obs) return;
    const startSource = isConfirmed
      ? obs.executed_start_date || obs.start_date
      : obs.start_date;
    const endSource = isConfirmed
      ? obs.executed_end_date || obs.end_date
      : obs.end_date;
    setStartDate(startSource ? toDatetimeLocalValue(startSource) : "");
    setEndDate(endSource ? toDatetimeLocalValue(endSource) : "");
    setDescription(obs.description || "");
    setSubmitting(false);
    setError("");
  }, [show, obs, isConfirmed]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!startDate || !endDate) {
      setError(t("Informe o início e o fim do período."));
      return;
    }
    if (new Date(endDate) <= new Date(startDate)) {
      setError(
        isConfirmed
          ? t("O fim da execução deve ser depois do início.")
          : t("O fim do período solicitado deve ser depois do início.")
      );
      return;
    }
    setSubmitting(true);
    setError("");
    const payload = {
      description: description.trim(),
    };
    if (isConfirmed) {
      payload.executed_start_date = toLocalISOString(startDate);
      payload.executed_end_date = toLocalISOString(endDate);
    } else {
      payload.start_date = toLocalISOString(startDate);
      payload.end_date = toLocalISOString(endDate);
    }
    try {
      await onSave(payload);
    } catch (err) {
      const detail =
        err &&
        err.response &&
        err.response.data &&
        (err.response.data.detail ||
          err.response.data.executed_end_date ||
          err.response.data.end_date);
      setError(
        (Array.isArray(detail) ? detail[0] : detail) ||
          t("Não foi possível salvar as alterações. Tente novamente.")
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal open={!!show} onClose={onHide}>
      <div className="modal-size" style={{ maxWidth: 520 }}>
        <div className="x_title">
          <h5 align="center">
            <FaEdit style={{ marginRight: 6 }} />
            {dialogTitle || t("Editar ordem de serviço")}
          </h5>
          <div className="clearfix" />
        </div>
        <form onSubmit={handleSubmit} style={{ padding: 10 }}>
          <p style={{ marginBottom: 8, fontWeight: 600 }}>{title}</p>
          {farmLabel ? (
            <p style={{ marginBottom: 8, color: "#555" }}>
              <strong>{t("Fazenda")}:</strong> {farmLabel}
            </p>
          ) : null}
          {isConfirmed && obs && obs.start_date ? (
            <p style={{ marginBottom: 14, color: "#666", fontSize: 13 }}>
              {t("Período solicitado")}:{" "}
              {formatObservationDateTime(obs.start_date)} →{" "}
              {formatObservationDateTime(obs.end_date)}
            </p>
          ) : (
            <p style={{ marginBottom: 14, color: "#555" }}>
              {t(
                "Altere só o período e as observações desta ordem. O restante da planilha permanece igual."
              )}
            </p>
          )}
          <div className="form-group">
            <label htmlFor="so-card-edit-start">
              <FaCalendarAlt style={{ marginRight: 5 }} />
              {isConfirmed
                ? t("Início da execução")
                : t("Início do período solicitado")}
            </label>
            <input
              id="so-card-edit-start"
              type="datetime-local"
              className="form-control"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              required
              autoFocus
            />
          </div>
          <div className="form-group">
            <label htmlFor="so-card-edit-end">
              <FaCalendarAlt style={{ marginRight: 5 }} />
              {isConfirmed
                ? t("Fim da execução")
                : t("Fim do período solicitado")}
            </label>
            <input
              id="so-card-edit-end"
              type="datetime-local"
              className="form-control"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              required
            />
          </div>
          <div className="form-group">
            <label htmlFor="so-card-edit-notes">
              {notesLabel || t("Observações")}
            </label>
            <textarea
              id="so-card-edit-notes"
              className="form-control"
              rows={4}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
              placeholder={t(
                "Observações para o gestor e para o operador no campo"
              )}
            />
          </div>
          {error ? <div className="alert alert-danger">{error}</div> : null}
          <div className="text-right" style={{ marginTop: 15 }}>
            <button
              type="button"
              className="btn btn-default"
              onClick={onHide}
              disabled={submitting}
              style={{ marginRight: 8 }}
            >
              <FaTimes style={{ marginRight: 5 }} />
              {t("Cancelar")}
            </button>
            <button
              type="submit"
              className="btn btn-success"
              disabled={submitting || !startDate || !endDate}
            >
              <FaSave style={{ marginRight: 5 }} />
              {submitting ? t("Salvando...") : t("Salvar alterações")}
            </button>
          </div>
        </form>
      </div>
    </Modal>
  );
}

export default ServiceOrderCardEditModal;
